<template>
  <div class="c-chat__messagesContainer">
    <div
      v-if="!chatSearchMessagesHistoryStore.sortedMessages.length && !chatSearchMessagesHistoryStore.searchingMessages"
      class="t-body3 u-colorDarkMediumEmphasis s-text-center u-margin3">{{ $t('chat.no_messages_found') }}</div>
    <DynamicScroller
      v-show="chatSearchMessagesHistoryStore.sortedMessages && !chatSearchMessagesHistoryStore.searchingMessages"
      ref="messagesScroller"
      :items="chatSearchMessagesHistoryStore.sortedMessages"
      :min-item-size="96"
      class="c-chat__messagesScroller">
      <template #default="{ item, index, active }">
        <DynamicScrollerItem
          :item="item"
          :active="active"
          :size-dependencies="[
            item.content,
            item.metadata?.uploading,
            item.metadata?.transcript,
          ]"
          :data-index="index"
          :data-message-id="item.id"
          class="c-chat__messageRow"
          :class="{ '-me': isMe(item.senderDisplayName) }">
          <div>
            <div class="o-flex -alignCenter -gap4">
              <Message
                :key="item.id"
                :message="item" />
            </div>
          </div>
        </DynamicScrollerItem>
      </template>
    </DynamicScroller>
    <div
      v-if="chatSearchMessagesHistoryStore.searchingMessages"
      class="s-text-center u-margin3">
      <LoadingSpinner />
    </div>
  </div>
</template>

<script setup>

import {
  defineProps, ref, onMounted, onBeforeUnmount,
} from 'vue'
import { DynamicScroller, DynamicScrollerItem } from 'vue-virtual-scroller'

import LoadingSpinner from '@/components/LoadingSpinner.vue'
import { useChatSearchMessagesHistoryStore } from '@/modules/chat/chatSearchMessagesHistoryStore'
import Message from '@/modules/chat/components/Message.vue'
import store from '@/store'
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css'

defineProps({
  chatThread: {
    type: Object,
    required: true,
  },
})

const chatSearchMessagesHistoryStore = useChatSearchMessagesHistoryStore()

let olderMessagesAreLoading = false
const messagesScroller = ref()

function updateScrollbarPosition(messageId) {
  const newItemIndex = messagesScroller.value.items.findIndex((item) => item.id === messageId)
  return messagesScroller.value.scrollToItem(newItemIndex)
}

function isMe(userId) {
  return parseInt(userId) === store.getters['auth/userId']
}

async function init() {
  await chatSearchMessagesHistoryStore.init(messagesScroller)
}
init()

async function onScroll() {
  if (olderMessagesAreLoading || chatSearchMessagesHistoryStore.searchingMessages || chatSearchMessagesHistoryStore.allMessagesAreLoaded) {
    return
  }
  if (messagesScroller.value.$el.scrollTop > 0) {
    return
  }

  olderMessagesAreLoading = true

  // we always want to scroll to the message which was on the top message before loading messages
  const messageId = chatSearchMessagesHistoryStore.sortedMessages[0].id

  await updateScrollbarPosition(messageId)

  olderMessagesAreLoading = false
}

onMounted(async () => {
  messagesScroller.value.$el.addEventListener('scroll', onScroll)
})

onBeforeUnmount(() => {
  messagesScroller.value.$el.removeEventListener('scroll', onScroll)
})
</script>

<style scoped>
.s-text-center {
  text-align: center;
}
</style>
